import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Dropdown, Input, Loading } from '@/components'
import { INVOICE_TYPES } from '@/consts/billing'
import type { InvoiceStepProps } from '@/types/billing'
import { validateMobileBarcode } from '@/utils/billing'

export function InvoiceStep({
  formData,
  loadingStates,
  matchedOrganization,
  errors,
  onInvoiceTypeChange,
  onFieldChange,
  onShowCharityModal,
}: InvoiceStepProps) {
  const { t } = useTranslation('billing')

  // Create translated invoice type options
  const translatedInvoiceTypes = useMemo(
    () =>
      INVOICE_TYPES.map((type) => ({
        value: type.value,
        label: t(
          `invoiceModal.form.invoiceTypes.${
            type.value === 'mobile-barcode'
              ? 'mobileBarcode'
              : type.value === 'unified-business'
                ? 'unifiedBusiness'
                : type.value
          }`,
        ),
      })),
    [t],
  )
  // Show loading state while fetching invoice title data
  if (loadingStates.fetchingTitle) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loading />
      </div>
    )
  }

  return (
    <div>
      {/* Invoice Type Dropdown */}
      <div className="mb-4">
        <label className="block mb-2 t-sb">
          <Trans ns="billing" i18nKey="invoiceModal.form.invoiceType">
            Invoice type
          </Trans>
        </label>
        <Dropdown
          options={translatedInvoiceTypes}
          value={formData.invoiceType}
          placeholder={t('invoiceModal.form.selectInvoiceType')}
          onChange={(value) => onInvoiceTypeChange(value as any)}
        />
      </div>

      {/* Mobile Barcode Fields */}
      {formData.invoiceType === 'mobile-barcode' && (
        <>
          <div className="mb-4">
            <label className="block mb-2 t-sb">
              <Trans ns="billing" i18nKey="invoiceModal.form.name">
                Name
              </Trans>
            </label>
            <Input
              value={formData.name}
              placeholder={t('invoiceModal.form.namePlaceholder')}
              onChange={(e) => onFieldChange('name', e.target.value)}
              maxLength={30}
            />
            {errors.validationErrors.name && (
              <div className="text-error t-sm mt-1">{errors.validationErrors.name}</div>
            )}
          </div>
          <div className="mb-4">
            <label htmlFor="mobile-barcode" className="block mb-2 t-sb">
              <Trans ns="billing" i18nKey="invoiceModal.form.mobileBarcode">
                Mobile Barcode
              </Trans>
            </label>
            <Input
              id="mobile-barcode"
              value={formData.mobileBarcode}
              placeholder={t('invoiceModal.form.mobileBarcodePlaceholder')}
              onChange={(e) => onFieldChange('mobileBarcode', e.target.value.toUpperCase())}
              maxLength={8}
              aria-invalid={Boolean(errors.validationErrors.mobileBarcode)}
              aria-describedby={
                errors.validationErrors.mobileBarcode
                  ? 'mobile-barcode-error'
                  : 'mobile-barcode-help'
              }
              validator={(value) => {
                if (!value) return true
                return validateMobileBarcode(value)
              }}
            />
            <div
              id={
                errors.validationErrors.mobileBarcode
                  ? 'mobile-barcode-error'
                  : 'mobile-barcode-help'
              }
              className={`${errors.validationErrors.mobileBarcode ? 'text-error' : 'text-secondary'} t-sm mt-1`}
              role={errors.validationErrors.mobileBarcode ? 'alert' : undefined}
            >
              {errors.validationErrors.mobileBarcode || t('invoiceModal.form.mobileBarcodeHint')}
            </div>
          </div>
        </>
      )}

      {/* Unified Business Fields */}
      {formData.invoiceType === 'unified-business' && (
        <>
          <div className="mb-4">
            <label className="block mb-2 t-sb">
              <Trans ns="billing" i18nKey="invoiceModal.form.businessNumber">
                Unified Business Number
              </Trans>
            </label>
            <Input
              value={formData.businessId}
              placeholder={t('invoiceModal.form.businessNumberPlaceholder')}
              onChange={(e) => onFieldChange('businessId', e.target.value)}
              validator={(value) => {
                if (!value) return true
                return /^\d{8}$/.test(value)
              }}
            />
            {errors.validationErrors.businessId && (
              <div className="text-error t-sm mt-1">{errors.validationErrors.businessId}</div>
            )}
          </div>
          <div className="mb-4">
            <label className="block mb-2 t-sb">
              <Trans ns="billing" i18nKey="invoiceModal.form.companyName">
                Company name
              </Trans>
            </label>
            <Input
              value={formData.businessName}
              placeholder={t('invoiceModal.form.companyNamePlaceholder')}
              onChange={(e) => onFieldChange('businessName', e.target.value)}
            />
            {formData.businessName.length > 60 && (
              <div className="text-orange-600 t-sm mt-1">
                {t('invoiceModal.validation.businessNameMaxWarning')}
              </div>
            )}
            {errors.validationErrors.businessName && (
              <div className="text-error t-sm mt-1">{errors.validationErrors.businessName}</div>
            )}
          </div>
          <div className="mb-4">
            <label className="block mb-2 t-sb">
              <Trans ns="billing" i18nKey="invoiceModal.form.email">
                Email
              </Trans>
            </label>
            <Input
              value={formData.email}
              placeholder={t('invoiceModal.form.emailPlaceholder')}
              onChange={(e) => onFieldChange('email', e.target.value)}
              validator={(value) => {
                if (!value) return true
                if (value.length > 50) return false
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
              }}
            />
            {errors.validationErrors.email && (
              <div className="text-error t-sm mt-1">{errors.validationErrors.email}</div>
            )}
          </div>
        </>
      )}

      {/* Donation Fields */}
      {formData.invoiceType === 'donation' && (
        <>
          <div className="mb-4">
            <label className="block mb-2 t-sb">
              <Trans ns="billing" i18nKey="invoiceModal.form.name">
                Name
              </Trans>
            </label>
            <Input
              value={formData.name}
              placeholder={t('invoiceModal.form.namePlaceholder')}
              onChange={(e) => onFieldChange('name', e.target.value)}
              maxLength={30}
            />
            {errors.validationErrors.name && (
              <div className="text-error t-sm mt-1">{errors.validationErrors.name}</div>
            )}
          </div>
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="t-sb">
                <Trans ns="billing" i18nKey="invoiceModal.form.recipientOrganization">
                  Recipient Organization
                </Trans>
              </label>
              <a
                href="#"
                className="text-highlight hover:text-highlight t-x-sb"
                onClick={(e) => {
                  e.preventDefault()
                  onShowCharityModal()
                }}
              >
                <Trans ns="billing" i18nKey="invoiceModal.form.searchCharity">
                  Search charity organization donation code
                </Trans>
              </a>
            </div>
            <Input
              value={formData.charityCode || ''}
              placeholder={t('invoiceModal.form.charityCodePlaceholder')}
              onChange={(e) => onFieldChange('charityCode', e.target.value)}
            />
            {formData.charityCode && (
              <div className="text-secondary t-sm mt-2">
                {loadingStates.searchingCharity ? (
                  <span className="flex items-center gap-1">
                    <Loading className="h-4 w-4" />
                    <Trans ns="billing" i18nKey="invoiceModal.form.searching">
                      Searching...
                    </Trans>
                  </span>
                ) : matchedOrganization ? (
                  <span>{matchedOrganization.name}</span>
                ) : errors.searchError ? (
                  <span>{errors.searchError}</span>
                ) : null}
              </div>
            )}
            {errors.validationErrors.charityCode && (
              <div className="text-error t-sm mt-1">{errors.validationErrors.charityCode}</div>
            )}
          </div>
        </>
      )}

      {/* Electronic Invoice Fields */}
      {formData.invoiceType === 'electronic' && (
        <>
          <div className="mb-4 t-sm text-secondary">
            <Trans ns="billing" i18nKey="invoiceModal.form.electronicInvoiceNote">
              The invoices have been hosted in accordance with the Ministry of Finance regulations,
              paper invoices will no longer be issued and electronic invoices will be sent by
              mailbox.
            </Trans>
          </div>
          <div className="mb-4">
            <label className="block mb-2 t-sb">
              <Trans ns="billing" i18nKey="invoiceModal.form.name">
                Name
              </Trans>
            </label>
            <Input
              value={formData.name}
              placeholder={t('invoiceModal.form.namePlaceholder')}
              onChange={(e) => onFieldChange('name', e.target.value)}
              maxLength={30}
            />
            {errors.validationErrors.name && (
              <div className="text-error t-sm mt-1">{errors.validationErrors.name}</div>
            )}
          </div>
          <div className="mb-4">
            <label className="block mb-2 t-sb">
              <Trans ns="billing" i18nKey="invoiceModal.form.email">
                Email
              </Trans>
            </label>
            <Input
              value={formData.email}
              placeholder={t('invoiceModal.form.emailPlaceholder')}
              onChange={(e) => onFieldChange('email', e.target.value)}
              validator={(value) => {
                if (!value) return true
                if (value.length > 50) return false
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
              }}
            />
            {errors.validationErrors.email && (
              <div className="text-error t-sm mt-1">{errors.validationErrors.email}</div>
            )}
          </div>
        </>
      )}

      {/* API Error Display */}
      {errors.apiError && (
        <div className="bg-error/10 border border-error/40 rounded-md p-3 mb-4">
          <div className="text-error t-sm">{errors.apiError}</div>
        </div>
      )}
    </div>
  )
}
